import SelectComponent from "@via/components/select/select.tsx";
import {
  EditProcessGroupOptions,
  QualifierOptions,
  QualifierOptionsEnum,
} from "@via/pages/projects/pipelines/workflow/utils/constants.tsx";
import { SelectOption } from "@via/utils/interfaces.tsx";
import { useMemo, useState } from "react";
import { EyeIcon } from "@heroicons/react/24/outline";

export default function ManageParametersModal({
  action,
  parameters,
  onCancel,
  onSubmit,
}) {
  const [identifier, setIdentifier] = useState<string>("");
  const [selectedQualifier, setSelectedQualifier] =
    useState<SelectOption | null>(null);
  const [fileType, setFileType] = useState<string>("");
  const [selectedParameter, setSelectedParameter] = useState<SelectOption>();
  const [isShowParameter, setIsShowParameter] = useState<boolean>(
    action === EditProcessGroupOptions.EditParameter
  );

  const updateQualifier = val => {
    setSelectedQualifier(val);
  };

  const handleParameterAutofill = selected => {
    const newQualifier = parameters?.find(
      item => item?.qualifier === selected?.qualifier
    );
    const option =
      QualifierOptions.find(
        option => newQualifier.qualifier === option.value
      ) ?? null;
    setSelectedQualifier(option);
    setFileType(selected?.fileType);
    setIdentifier(selected?.identifier);
    setSelectedParameter(selected);
  };

  const handleParameterUpdate = selected => {
    setSelectedParameter(selected);
  };

  const handleSubmit = (action: string): void => {
    let payload;
    switch (action) {
      case EditProcessGroupOptions.CreateParameter:
        payload = {
          name: identifier,
          qualifier: selectedQualifier?.value,
          fileType:
            selectedQualifier?.value === QualifierOptionsEnum.Val
              ? identifier
              : fileType,
        };
        break;
      case EditProcessGroupOptions.EditParameter:
        payload = {
          id: selectedParameter?.value,
          name: identifier,
          qualifier: selectedQualifier?.value,
          fileType:
            selectedQualifier?.value === QualifierOptionsEnum.Val
              ? identifier
              : fileType,
        };
        break;
      case EditProcessGroupOptions.DeleteParameter:
        payload = selectedParameter?.value;
        break;
      default:
        break;
    }
    onSubmit(payload);
  };

  const renderHeader = useMemo(() => {
    return (
      <div className="modal-header">
        <h3 className="mb-4 text-lg font-medium leading-6 text-gray-900">
          {action === EditProcessGroupOptions.CreateParameter
            ? "Create Parameter"
            : action === EditProcessGroupOptions.EditParameter
              ? "Edit Parameter"
              : "Delete Parameter"}
        </h3>
      </div>
    );
  }, [action]);

  const renderFormFields = () => (
    <>
      <div className="my-4">
        <label className="block pb-2 text-sm font-medium leading-6 text-gray-900">
          Parameter
        </label>
        {isShowParameter ? (
          <SelectComponent
            name="parameter"
            value={selectedParameter}
            enableClear={false}
            options={parameters}
            formatOptionLabel={(option: any, { context }) =>
              context === "menu" ? (
                <span>
                  {option.label} ({option.fileType}, {option.qualifier})
                </span>
              ) : (
                option.label
              )
            }
            onChange={handleParameterAutofill}
            placeholder="Select a Parameter"
          />
        ) : (
          <button
            id="button-show-parameters"
            className="cursor-pointer mr-1 h-9 max-w-9 rounded-md border-[1px] px-1.5"
            onClick={() => setIsShowParameter(true)}
            title="Show Parameters">
            <EyeIcon className="h-5 w-5" />
          </button>
        )}
      </div>
      <label className="block text-sm font-medium leading-6 text-gray-900">
        Identifier
        <span className="pl-1 text-red-400">*</span>
      </label>
      <input
        className="mt-1.5 block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
        value={identifier}
        onChange={e => setIdentifier(e.target.value)}
      />
      <div className="mb-4">
        <label className="mb-1.5 mt-4 block text-sm font-medium leading-6 text-gray-900">
          Qualifier
          <span className="pl-1 text-red-400">*</span>
        </label>
        <SelectComponent
          name="chooseQualifier"
          value={selectedQualifier}
          enableClear={false}
          options={QualifierOptions}
          onChange={updateQualifier}
          placeholder="Select a Qualifier"
        />
      </div>
      {renderFileType}
    </>
  );

  const renderFileType = useMemo(() => {
    if (selectedQualifier?.value === QualifierOptionsEnum.Val) return null;
    return (
      <>
        <label className="block text-sm font-medium leading-6 text-gray-900">
          {selectedQualifier?.value === QualifierOptionsEnum.File ||
          selectedQualifier?.value === QualifierOptionsEnum.Set
            ? "File Type"
            : "File Type/Identifier"}
          <span className="pl-1 text-red-400">*</span>
        </label>
        <input
          className="mb-6 mt-1.5 block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
          value={fileType}
          onChange={e => setFileType(e.target.value)}
        />
      </>
    );
  }, [selectedQualifier, fileType]);

  const renderFooter = useMemo(() => {
    return (
      <div className="modal-footer flex justify-end space-x-2">
        <button
          type="button"
          className="rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
          onClick={() => onCancel()}>
          Cancel
        </button>
        <button
          type="button"
          className="ml-4 inline-flex justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
          onClick={() => handleSubmit(action)}>
          Submit
        </button>
      </div>
    );
  }, [action, handleSubmit, onCancel]);

  return (
    <div className="w-[400px] max-w-[500px]">
      {renderHeader}
      {action !== EditProcessGroupOptions.DeleteParameter && renderFormFields()}
      {action === EditProcessGroupOptions.DeleteParameter && (
        <>
          <p className="pt-1 text-xs text-gray-500">
            Select a Parameter below and choose Submit in order to delete.
          </p>
          <div className="my-4">
            <SelectComponent
              name="parameter"
              value={selectedParameter}
              enableClear={false}
              options={parameters}
              onChange={handleParameterUpdate}
              placeholder={"Select a Parameter"}
            />
          </div>
        </>
      )}
      {renderFooter}
    </div>
  );
}
